import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        affiliate_type: user.affiliate_type || "",
        associate_firm: user.associate_firm || "",
        bank_details: user.bank_details || { account_holder: "", account_number: "", ifsc: "", bank_name: "" },
      });
      api.get("/admin/affiliate-types").then((r) => setTypes(r.data));
    } else {
      setForm(null);
    }
  }, [user]);

  if (!user || !form) return null;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setBank = (k, v) => setForm({ ...form, bank_details: { ...form.bank_details, [k]: v } });
  const isAffiliate = user.role === "affiliate";
  const bankFilled = Object.values(form.bank_details).some((v) => v && String(v).trim());

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
      };
      if (isAffiliate) {
        payload.affiliate_type = form.affiliate_type || null;
        payload.associate_firm = form.associate_firm || null;
        if (bankFilled) payload.bank_details = form.bank_details;
      }
      const { data } = await api.patch(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle>Edit {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-slate-300">Name</Label>
            <Input data-testid="edit-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="velo-input mt-2" />
          </div>
          <div>
            <Label className="text-slate-300">Phone number</Label>
            <Input data-testid="edit-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="velo-input mt-2 font-mono" />
            <p className="text-[11px] text-slate-500 mt-1.5">Changing phone or name regenerates the login code.</p>
          </div>
          {isAffiliate ? (
            <>
              <div>
                <Label className="text-slate-300">Affiliate type</Label>
                <Select value={form.affiliate_type} onValueChange={(v) => set("affiliate_type", v)}>
                  <SelectTrigger data-testid="edit-affiliate-type" className="velo-input mt-2"><SelectValue placeholder="Select type…" /></SelectTrigger>
                  <SelectContent className="bg-[#22103F] border-white/10 text-white">
                    {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Associate firm <span className="text-slate-500 text-xs">(optional)</span></Label>
                <Input data-testid="edit-associate-firm" value={form.associate_firm} onChange={(e) => set("associate_firm", e.target.value)} placeholder="e.g. Star Motors Kochi" className="velo-input mt-2" />
              </div>
              <div className="pt-3 border-t border-white/10">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Bank details (for cash payouts)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-slate-300 text-xs">Account holder</Label><Input data-testid="edit-bank-holder" value={form.bank_details.account_holder} onChange={(e) => setBank("account_holder", e.target.value)} className="velo-input mt-1 h-9" /></div>
                  <div><Label className="text-slate-300 text-xs">Bank name</Label><Input data-testid="edit-bank-name" value={form.bank_details.bank_name} onChange={(e) => setBank("bank_name", e.target.value)} className="velo-input mt-1 h-9" /></div>
                  <div><Label className="text-slate-300 text-xs">Account number</Label><Input data-testid="edit-bank-number" value={form.bank_details.account_number} onChange={(e) => setBank("account_number", e.target.value)} className="velo-input mt-1 h-9 font-mono" /></div>
                  <div><Label className="text-slate-300 text-xs">IFSC</Label><Input data-testid="edit-bank-ifsc" value={form.bank_details.ifsc} onChange={(e) => setBank("ifsc", e.target.value)} className="velo-input mt-1 h-9 font-mono" /></div>
                </div>
              </div>
            </>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5">Cancel</Button>
          <Button data-testid="btn-save-edit-user" onClick={save} disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
